// What a rated charge source looks like under load.
//
// **The problem this fixes is legibility, not physics.** A charge network
// settles at `V = SumQ / SumC` over every locus on it (`queries.networkPotential`),
// so a Font holding 8 at capacity 1 reads 8 alone, 4 with one capacity-1
// Terminal attached, and 2.67 with two. That is the whole subject of a charge
// puzzle -- and until now nothing in the world showed it. A player who wired a
// second consumer onto a working line watched *both* consumers go dark and had
// exactly one binary observation ("neither is lit") from which to deduce a
// division rule. Charter Rule 4 asks for a visible proxy for hidden state; a
// pair of unlit lamps is a proxy for the *consequence*, and none at all for the
// cause.
//
// So the source itself says how hard it is being drawn on, on the object
// causing the problem, in the same turn the wiring changes. It still says
// "overdrawn" rather than "V = 8/3": the fact is shown, the rule is still the
// player's to deduce.
//
// **The rungs are derived from the consumers, not from a number on the source.**
// A threshold authored here would be a second copy of a fact the consumers
// already state, and it would be wrong the moment a room wired two consumers
// with different appetites. Instead: ask every `conduit.feed` on this source's
// network whether it is currently satisfied, using the same `isConduitCharged`
// the gates themselves read, and count.
//
//   every consumer satisfied   fed         the line carries what is asked of it
//   some but not all           straining   V has fallen past the hungriest
//   none                       overdrawn   V has fallen past all of them
//
// No consumers at all is `fed`, not `overdrawn`: a Font with nothing wired to it
// is idle, not failing, and room8's and room11's Fonts are exactly that.
//
// **Cheap, live and reversible**, the same shape as `relic.ts`: the rung is
// recomputed every settle from what is attached right now, and unwiring a
// consumer puts the sprite back.
//
// One thing *is* stored, and it is stored to protect authored content rather
// than to remember a stage. The ladder owns the two loaded rungs outright and
// hands the idle one back to whatever the author wrote, because the four Fonts
// in the world do not read alike -- one is set flush into a metal floor, one
// refills a moment after it is drawn off, one teaches that marking it lets
// something else draw from it. Those lines say what that *placement* is for,
// which is not a thing a ladder can recompute and not a thing it should be
// replacing with one generic sentence. So `chargeSource.authored` records the
// pair on the first settle and `fed` restores it, exactly as
// `barrier.untransformed` does for a toggle.
import { ObjectDef, ChargeSourceStageId, ChargeSourceVariant, RoomDef } from './types';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { applyStagePresentation } from './presentation';
import { chargeNetwork, isConduitCharged } from './queries';

/** The look of each rung, shared by every variant -- what a source under load
 * *is* does not depend on what it is called. */
export const CHARGE_SOURCE_STAGE_TEXTURE: Record<ChargeSourceVariant, Record<ChargeSourceStageId, string>> = {
  font: {
    fed: 'object-charge-font',
    straining: 'object-charge-font-straining',
    overdrawn: 'object-charge-font-overdrawn',
  },
};

/** How strongly each rung reads when Measured (`ObjectDef.signature`).
 *
 * Derived rather than authored, for `relic.ts`'s reason: a Measure·Radial that
 * kept saying 0.85 at a Font whose line has collapsed to a third of its reading
 * would be the overlay contradicting the sprite, which is the exact bug the
 * relic ladder was rewritten to end. The `fed` value is the one every authored
 * Font carries, so an un-wired world Measures exactly as it always did. */
export const CHARGE_SOURCE_STAGE_SIGNATURE: Record<ChargeSourceStageId, number> = {
  fed: 0.85,
  straining: 0.5,
  overdrawn: 0.25,
};

export interface ChargeSourceStageMeta {
  name: string;
  description: string;
}

/** Name and flavour per variant × rung. The variant is the noun, the rung is
 * the adjective -- `RELIC_LADDER`'s grid exactly.
 *
 * The descriptions carry the teaching, and deliberately stop short of the rule:
 * each says what the basin is doing and that there is *more asking than there
 * is to give*, without ever saying that potential divides between stores.
 *
 * **`fed` is a fallback, not the usual answer.** An idle source is presented
 * from `chargeSource.authored` instead, so a placement keeps whatever its author
 * wrote about it; this row is what a source with no authored description at all
 * falls back to, and the name every Font goes back to being. */
export const CHARGE_SOURCE_LADDER: Record<ChargeSourceVariant, Record<ChargeSourceStageId, ChargeSourceStageMeta>> = {
  font: {
    fed: {
      name: 'Charged Font',
      description: 'A stone basin, charge crackling faintly across water that never quite settles.',
    },
    straining: {
      name: 'Straining Font',
      description:
        'A stone basin pulled thin, its arc stuttering amber across the water. Something on the line is still getting what it needs. Something else is not.',
    },
    overdrawn: {
      name: 'Overdrawn Font',
      description:
        'A stone basin gone dim and red, the arc guttering low over the water. It holds no less than it ever did -- there is simply more asking of it than it has to give, and so nothing on the line is getting enough.',
    },
  },
};

/** Every `conduit.feed` drawing on the same network as `source`.
 *
 * Note what this does *not* include: a feed elsewhere in the room that names
 * this source through `standingPillarId`. That consumer reads the source's
 * potential without being wired to it, so it is a real draw in the puzzle's
 * sense and not one in the network's -- it adds no capacity and divides
 * nothing. Counting it would make a Font read "overdrawn" for a load that costs
 * it nothing, which is precisely the confusion this ladder exists to remove. */
export function chargeSourceConsumers(room: RoomDef, source: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): ObjectDef[] {
  return chargeNetwork(room, source, physics)
    .flatMap((locus) => (locus.kind === 'object' ? [locus.obj] : []))
    .filter((obj) => obj.conduit?.feed !== undefined);
}

/** Which rung `source` currently reads. Pure, and cheap enough to call every
 * settle -- what is attached right now is the only state there is. */
export function chargeSourceStage(room: RoomDef, source: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): ChargeSourceStageId {
  const consumers = chargeSourceConsumers(room, source, physics);
  if (consumers.length === 0) return 'fed';
  const satisfied = consumers.filter((obj) => isConduitCharged(room, obj, physics)).length;
  if (satisfied === consumers.length) return 'fed';
  return satisfied === 0 ? 'overdrawn' : 'straining';
}

/** Re-derives a source's whole presentation -- name, texture, description and
 * Measured signature -- from the rung it currently reads.
 *
 * Writes every field back rather than only comparing, for the reason
 * `refreshRelicState` gives at length: this ladder is reversible, so a rung that
 * cycles back to the authored look would compare equal to a stale field and
 * skip re-emitting. */
export function refreshChargeSource(room: RoomDef, obj: ObjectDef, physics: PhysicsConfig, out: SimEvents): void {
  if (!obj.chargeSource) return;
  const variant = obj.chargeSource.variant ?? 'font';
  const stage = chargeSourceStage(room, obj, physics);
  const ladder = CHARGE_SOURCE_LADDER[variant];

  // Recorded before anything is written, on the same reasoning `resolveBarrier`
  // records `barrier.untransformed`: the authored pair cannot be recomputed, and
  // a ladder that cannot put it back is a ladder that destroys it. Here that is
  // not hypothetical -- the four shipped Fonts each say something different and
  // specific about their own placement, and the generic line is what they would
  // all have become on the first settle.
  obj.chargeSource.authored ??= { name: obj.name, description: obj.interact?.text ?? ladder.fed.description };

  // **The ladder owns the loaded rungs and hands the idle one back.** A Font
  // under no load is just the Font the author wrote; a Font that cannot keep up
  // is a thing the ladder is describing, and its own flavour is not what the
  // player needs in that moment.
  const meta = stage === 'fed' ? obj.chargeSource.authored : ladder[stage];

  applyStagePresentation(obj, { name: meta.name, texture: CHARGE_SOURCE_STAGE_TEXTURE[variant][stage], description: meta.description }, out, 'cross-fade');

  const signature = CHARGE_SOURCE_STAGE_SIGNATURE[stage];
  if (obj.signature?.charge !== signature) obj.signature = { ...obj.signature, charge: signature };
}

/** The declared system's own body (see settle.ts). */
export function settleChargeSources(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  for (const obj of room.objects) {
    if (obj.chargeSource) refreshChargeSource(room, obj, world.physics, out);
  }
}
